/**
 * TaxonomyTab — declared-catalog CRUD for one KB.
 *
 * Operators can upsert/delete topics, entity types, entities, aliases,
 * identifiers, and both relation kinds. ``system`` seeded rows remain
 * editable (name/description, …) per the repo semantics, but deletes
 * are blocked in the UI to avoid shredding the seeded baseline.
 *
 * Predicates mirror the extraction catalog and remain read-only here —
 * add them via extraction candidate promotion instead.
 */

import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import {
  deleteAlias,
  deleteEntity,
  deleteEntityRelation,
  deleteEntityType,
  deleteIdentifier,
  deleteTopic,
  deleteTopicRelation,
  getTaxonomy,
  listPredicates,
  upsertAlias,
  upsertEntity,
  upsertEntityRelation,
  upsertEntityType,
  upsertIdentifier,
  upsertTopic,
  upsertTopicRelation,
} from "@/lib/kb-extraction";
import {
  KB_ENTITY_RELATION_TYPES,
  KB_ENTITY_TOPIC_RELATION_TYPES,
  KB_IDENTIFIER_TYPES,
} from "@/lib/types";
import type {
  KbAliasView,
  KbEntityRelationView,
  KbEntityTypeView,
  KbEntityView,
  KbIdentifierKind,
  KbIdentifierView,
  KbPredicateView,
  KbTaxonomyBundle,
  KbTopicRelationView,
  KbTopicView,
  TaxonomySource,
  UpsertKbAliasRequest,
  UpsertKbEntityRelationRequest,
  UpsertKbEntityRequest,
  UpsertKbEntityTypeRequest,
  UpsertKbIdentifierRequest,
  UpsertKbTopicRelationRequest,
  UpsertKbTopicRequest,
} from "@/lib/types";

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

const SOURCE_USER = "user" as TaxonomySource;

function mutationError(err: unknown): string {
  if (err instanceof ApiError) return `${err.status} ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Request failed";
}

function blocksDeletion(source: TaxonomySource): boolean {
  return source === "system";
}

interface TaxonomyTabProps {
  kbId: string;
  refreshKey?: number;
  /** Optional parent hook so sibling panels (entity-type pick-lists)
   * invalidate when the taxonomy row set changes. */
  onCatalogChanged?: () => void;
}

interface TaxonomyState {
  bundle: KbTaxonomyBundle;
  predicates: KbPredicateView[];
}

export function TaxonomyTab({ kbId, refreshKey, onCatalogChanged }: TaxonomyTabProps) {
  const [state, setState] = useState<TaxonomyState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCatalog = useCallback(
    async (quiet: boolean) => {
      void refreshKey;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const [bundle, predicates] = await Promise.all([getTaxonomy(kbId), listPredicates(kbId)]);
        setState({ bundle, predicates });
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? `${err.status} ${err.message}`
            : err instanceof Error
              ? err.message
              : "Failed to load taxonomy";
        setError(msg);
        setState(null);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [kbId, refreshKey],
  );

  useEffect(() => {
    void loadCatalog(false);
  }, [loadCatalog]);

  const reloadQuiet = useCallback(async () => {
    await loadCatalog(true);
    onCatalogChanged?.();
  }, [loadCatalog, onCatalogChanged]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 text-xs text-muted-foreground">
        <span>
          Edit catalog rows inline. Deletes on <Badge variant="outline">system</Badge> seeded rows
          are disabled in the UI. Predicates remain read-only here — promote from the Extraction
          tab.
        </span>
        {state ? <CatalogCounts bundle={state.bundle} predicates={state.predicates} /> : null}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <div className="p-3">
            <ErrorBanner>{error}</ErrorBanner>
          </div>
        ) : loading && !state ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading catalog…
          </div>
        ) : state ? (
          <TaxonomyWorkspace kbId={kbId} state={state} onSaved={reloadQuiet} />
        ) : (
          <EmptyState className="m-3">No catalog rows yet.</EmptyState>
        )}
      </div>
    </div>
  );
}

function CatalogCounts({
  bundle,
  predicates,
}: {
  bundle: KbTaxonomyBundle;
  predicates: KbPredicateView[];
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 text-[11px]">
      <CountChip label="Topics" value={bundle.topics.length} />
      <CountChip label="Entity types" value={bundle.entity_types.length} />
      <CountChip label="Entities" value={bundle.entities.length} />
      <CountChip label="Aliases" value={bundle.aliases.length} />
      <CountChip label="Predicates" value={predicates.length} />
    </div>
  );
}

function CountChip({ label, value }: { label: string; value: number }) {
  return (
    <span className="rounded-full border border-border bg-card/50 px-2 py-0.5">
      <span className="font-medium text-foreground">{value}</span>{" "}
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

function TaxonomyWorkspace({
  kbId,
  state,
  onSaved,
}: {
  kbId: string;
  state: TaxonomyState;
  onSaved: () => Promise<void>;
}) {
  const [activeSubTab, setActiveSubTab] = useState<
    "topics" | "entities" | "predicates" | "relations"
  >("topics");

  return (
    <Tabs
      value={activeSubTab}
      onValueChange={(v) => setActiveSubTab(v as typeof activeSubTab)}
      className="flex h-full min-h-0 flex-col"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <TabsList className="h-7 p-0.5">
          <TabsTrigger value="topics" className="h-6 px-3">
            Topics
          </TabsTrigger>
          <TabsTrigger value="entities" className="h-6 px-3">
            Entities &amp; types
          </TabsTrigger>
          <TabsTrigger value="predicates" className="h-6 px-3">
            Predicates
          </TabsTrigger>
          <TabsTrigger value="relations" className="h-6 px-3">
            Relations
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="topics" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
        <TopicsPanel kbId={kbId} topics={state.bundle.topics} onSaved={onSaved} />
      </TabsContent>
      <TabsContent value="entities" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
        <EntitiesBundlePanel kbId={kbId} bundle={state.bundle} onSaved={onSaved} />
      </TabsContent>
      <TabsContent value="predicates" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
        <PredicatesTable predicates={state.predicates} />
      </TabsContent>
      <TabsContent value="relations" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
        <RelationsPanel kbId={kbId} bundle={state.bundle} onSaved={onSaved} />
      </TabsContent>
    </Tabs>
  );
}

// ───────────────────────────────────────────────────────────────── Topics
function TopicsPanel({
  kbId,
  topics,
  onSaved,
}: {
  kbId: string;
  topics: KbTopicView[];
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<UpsertKbTopicRequest | null>(null);
  const [topicToDelete, setTopicToDelete] = useState<KbTopicView | null>(null);
  const [busy, setBusy] = useState(false);

  const topicById = useMemo(() => new Map(topics.map((t) => [t.id, t])), [topics]);

  async function save(body: UpsertKbTopicRequest) {
    setBusy(true);
    try {
      await upsertTopic(kbId, body);
      toast.success(topics.some((x) => x.id === body.topic_id) ? "Topic updated" : "Topic created");
      setDraft(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!topicToDelete) return;
    setBusy(true);
    try {
      const resp = await deleteTopic(kbId, topicToDelete.id);
      if (resp.removed) {
        toast.success(
          `Topic removed (${resp.cascaded_topic_relations} topic-links, ${resp.reparented_topics} reparented)`,
        );
      } else {
        toast.warning("Topic was already gone");
      }
      setTopicToDelete(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={() =>
            setDraft({
              slug: "",
              name: "",
              description: "",
              parent_ids: [],
              source: SOURCE_USER,
            })
          }
        >
          <Plus className="h-3 w-3" />
          Add topic
        </Button>
      </div>
      {topics.length === 0 ? (
        <EmptyState>Create your first topic to seed classification.</EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Actions</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Parents</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="hidden md:table-cell">Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {topics.map((t) => {
              const parents = t.parent_ids
                .map((id) => topicById.get(id)?.name ?? id)
                .filter((label): label is string => Boolean(label));
              return (
                <TableRow key={t.id}>
                  <TableCell className="gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="mr-1 h-6 px-2 text-[10px]"
                      onClick={() =>
                        setDraft({
                          topic_id: t.id,
                          slug: t.slug,
                          name: t.name,
                          description: t.description ?? "",
                          parent_ids: t.parent_ids,
                          source: SOURCE_USER,
                        })
                      }
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[10px] text-destructive"
                      disabled={blocksDeletion(t.source)}
                      onClick={() => setTopicToDelete(t)}
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </Button>
                  </TableCell>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {parents.length === 0 ? "—" : parents.join(", ")}
                  </TableCell>
                  <TableCell>
                    <SourceBadge source={t.source} />
                  </TableCell>
                  <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
                    {t.description || "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {draft ? (
        <TopicUpsertDialog
          open
          kbId={kbId}
          initial={draft}
          topics={topics}
          submitting={busy}
          onCancel={() => setDraft(null)}
          onSubmit={save}
        />
      ) : null}

      <ConfirmDangerDialog
        open={topicToDelete != null}
        title="Delete topic?"
        description={`Permanently remove «${topicToDelete?.name ?? ""}». Child topics are reparented; entity↔topic links on this topic are cleared.`}
        confirmLabel={busy ? "Deleting…" : "Delete topic"}
        onCancel={() => setTopicToDelete(null)}
        disabled={busy}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}

function TopicUpsertDialog({
  open,
  kbId,
  initial,
  topics,
  submitting,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  kbId: string;
  initial: UpsertKbTopicRequest;
  topics: KbTopicView[];
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (body: UpsertKbTopicRequest) => void;
}) {
  const isEdit = Boolean(initial.topic_id);
  const excludeId = initial.topic_id;
  const [slug, setSlug] = useState(initial.slug ?? "");
  const [name, setName] = useState(initial.name ?? "");
  const [description, setDescription] = useState(initial.description ?? "");
  const [parentIds, setParentIds] = useState<string[]>(initial.parent_ids ?? []);

  useEffect(() => {
    if (!open) return;
    setSlug(initial.slug ?? "");
    setName(initial.name ?? "");
    setDescription(initial.description ?? "");
    setParentIds(initial.parent_ids ?? []);
  }, [open, initial.slug, initial.name, initial.description, initial.parent_ids]);

  const slugOk = SLUG_RE.test(slug.trim());
  const ok = slugOk && name.trim().length > 0;

  function toggleParent(pid: string) {
    setParentIds((prev) => (prev.includes(pid) ? prev.filter((id) => id !== pid) : [...prev, pid]));
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onCancel() : null)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit topic" : "Add topic"}</DialogTitle>
          <DialogDescription>
            Slugs are immutable once the row exists — the repository never rewrites slug on upsert (
            kb <span className="font-mono text-[11px]">{kbId.slice(0, 8)}…</span>)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="tx-slug">Slug</Label>
            <Input
              id="tx-slug"
              className="font-mono text-xs"
              disabled={isEdit}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            {!slugOk && slug.length > 0 ? (
              <p className="text-[11px] text-destructive">
                Use lowercase digits with single dashes.
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="tx-name">Display name</Label>
            <Input id="tx-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tx-desc">Description</Label>
            <Textarea
              id="tx-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Parents (multi-parent DAG)</Label>
            <div className="max-h-40 overflow-y-auto rounded-md border border-border p-2 text-xs">
              {topics
                .filter((t) => t.id !== excludeId)
                .map((t) => (
                  <label key={t.id} className="flex cursor-pointer items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={parentIds.includes(t.id)}
                      onChange={() => toggleParent(t.id)}
                    />
                    <span>{t.name}</span>
                    <span className="font-mono text-muted-foreground">({t.slug})</span>
                  </label>
                ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            disabled={!ok || submitting}
            onClick={() =>
              void onSubmit({
                topic_id: excludeId ?? null,
                slug: slug.trim(),
                name: name.trim(),
                description: description.trim() || null,
                parent_ids: parentIds,
                source: SOURCE_USER,
              })
            }
          >
            {submitting ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
            Save topic
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────── Entities / types / joins
function EntitiesBundlePanel({
  kbId,
  bundle,
  onSaved,
}: {
  kbId: string;
  bundle: KbTaxonomyBundle;
  onSaved: () => Promise<void>;
}) {
  return (
    <div className="space-y-8">
      <section>
        <h4 className="mb-2 text-sm font-medium">Entity types</h4>
        <EntityTypesPanel kbId={kbId} types={bundle.entity_types} onSaved={onSaved} />
      </section>
      <section>
        <h4 className="mb-2 text-sm font-medium">Entities</h4>
        <EntitiesSimplePanel kbId={kbId} bundle={bundle} onSaved={onSaved} />
      </section>
      <section>
        <h4 className="mb-2 text-sm font-medium">Aliases</h4>
        <AliasesPanel
          kbId={kbId}
          aliases={bundle.aliases}
          entities={bundle.entities}
          onSaved={onSaved}
        />
      </section>
      <section>
        <h4 className="mb-2 text-sm font-medium">Identifiers</h4>
        <IdentifiersPanel
          kbId={kbId}
          identifiers={bundle.identifiers}
          entities={bundle.entities}
          onSaved={onSaved}
        />
      </section>
    </div>
  );
}

function EntityTypesPanel({
  kbId,
  types,
  onSaved,
}: {
  kbId: string;
  types: KbEntityTypeView[];
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<UpsertKbEntityTypeRequest | null>(null);
  const [toDelete, setToDelete] = useState<KbEntityTypeView | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(body: UpsertKbEntityTypeRequest) {
    setBusy(true);
    try {
      await upsertEntityType(kbId, body);
      toast.success("Entity type saved");
      setDraft(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDel() {
    if (!toDelete) return;
    setBusy(true);
    try {
      await deleteEntityType(kbId, toDelete.id);
      toast.success("Entity type deleted");
      setToDelete(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <Button
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={() => setDraft({ slug: "", name: "", description: "", source: SOURCE_USER })}
        >
          <Plus className="h-3 w-3" />
          Add type
        </Button>
      </div>
      {types.length === 0 ? (
        <EmptyState>No entity types (unexpected — KB seed should populate defaults).</EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Actions</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="hidden md:table-cell">Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mr-1 h-6 px-2 text-[10px]"
                    onClick={() =>
                      setDraft({
                        entity_type_id: t.id,
                        slug: t.slug,
                        name: t.name,
                        description: t.description ?? "",
                        source: SOURCE_USER,
                      })
                    }
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-destructive"
                    disabled={blocksDeletion(t.source)}
                    onClick={() => setToDelete(t)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
                <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>
                  <SourceBadge source={t.source} />
                </TableCell>
                <TableCell className="hidden text-xs md:table-cell">
                  {t.description || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {draft ? (
        <Dialog open onOpenChange={(o) => (!o ? setDraft(null) : null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {draft.entity_type_id ? "Edit entity type" : "Add entity type"}
              </DialogTitle>
              <DialogDescription>
                Slug immutable after creation, same semantics as topics.
              </DialogDescription>
            </DialogHeader>
            <EntityTypeFields
              disabledSlug={Boolean(draft.entity_type_id)}
              slug={draft.slug}
              name={draft.name ?? ""}
              description={draft.description ?? ""}
              onChangeSlug={(slug) => setDraft((p) => (p ? { ...p, slug } : null))}
              onChangeName={(name) => setDraft((p) => (p ? { ...p, name } : null))}
              onChangeDescription={(desc) =>
                setDraft((p) => (p ? { ...p, description: desc } : null))
              }
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                disabled={busy || !draft.name.trim() || !SLUG_RE.test(draft.slug.trim())}
                onClick={() => void submit({ ...draft, source: SOURCE_USER })}
              >
                {busy ? <Loader2 className="animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <ConfirmDangerDialog
        open={toDelete != null}
        title="Delete entity type?"
        description={
          blocksDeletion(toDelete?.source ?? "")
            ? "System-seeded entity types cannot be deleted from the panel."
            : `Drop «${toDelete?.slug ?? ""}». Fails if entities still reference it.`
        }
        confirmLabel="Delete type"
        disabled={busy || (toDelete != null && blocksDeletion(toDelete.source))}
        onCancel={() => setToDelete(null)}
        onConfirm={() => void confirmDel()}
      />
    </div>
  );
}

function EntityTypeFields(props: {
  disabledSlug?: boolean;
  slug: string;
  name: string;
  description: string;
  onChangeSlug: (s: string) => void;
  onChangeName: (s: string) => void;
  onChangeDescription: (s: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label>Slug</Label>
        <Input
          disabled={props.disabledSlug}
          className="font-mono text-xs"
          value={props.slug}
          onChange={(e) => props.onChangeSlug(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input value={props.name} onChange={(e) => props.onChangeName(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Description</Label>
        <Textarea
          rows={2}
          value={props.description}
          onChange={(e) => props.onChangeDescription(e.target.value)}
        />
      </div>
    </div>
  );
}

function EntitiesSimplePanel({
  kbId,
  bundle,
  onSaved,
}: {
  kbId: string;
  bundle: KbTaxonomyBundle;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<UpsertKbEntityRequest | null>(null);
  const [toDelete, setToDelete] = useState<KbEntityView | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(body: UpsertKbEntityRequest) {
    setBusy(true);
    try {
      await upsertEntity(kbId, body);
      toast.success("Entity saved");
      setDraft(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDel() {
    if (!toDelete) return;
    setBusy(true);
    try {
      const resp = await deleteEntity(kbId, toDelete.id);
      if (resp.removed) {
        toast.success(`Entity deleted (aliases:${resp.cascaded_aliases}, …)`);
      }
      setToDelete(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  const { entities } = bundle;

  if (bundle.entity_types.length === 0) {
    return <EmptyState>Define at least one entity type first.</EmptyState>;
  }

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <Button
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={() =>
            setDraft({
              entity_type_id: bundle.entity_types[0].id,
              canonical_name: "",
              description: "",
              source: SOURCE_USER,
            })
          }
        >
          <Plus className="h-3 w-3" />
          Add entity
        </Button>
      </div>
      {entities.length === 0 ? (
        <EmptyState>No entities yet.</EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Actions</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="hidden md:table-cell">Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entities.map((e) => (
              <TableRow key={e.id}>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mr-1 h-6 px-2 text-[10px]"
                    onClick={() =>
                      setDraft({
                        entity_id: e.id,
                        canonical_name: e.canonical_name,
                        entity_type_id: e.entity_type_id,
                        description: e.description ?? "",
                        source: SOURCE_USER,
                      })
                    }
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-destructive"
                    disabled={blocksDeletion(e.source)}
                    onClick={() => setToDelete(e)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
                <TableCell className="font-medium">{e.canonical_name}</TableCell>
                <TableCell className="font-mono text-xs">
                  {bundle.entity_types.find((t) => t.id === e.entity_type_id)?.slug ?? "?"}
                </TableCell>
                <TableCell>
                  <SourceBadge source={e.source} />
                </TableCell>
                <TableCell className="hidden text-xs md:table-cell">
                  {e.description || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {draft ? (
        <Dialog open onOpenChange={(o) => (!o ? setDraft(null) : null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{draft.entity_id ? "Edit entity" : "Add entity"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Canonical name</Label>
                <Input
                  value={draft.canonical_name}
                  onChange={(e) =>
                    setDraft((p) => (p ? { ...p, canonical_name: e.target.value } : null))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Entity type</Label>
                <Select
                  value={draft.entity_type_id}
                  onValueChange={(v) => setDraft((p) => (p ? { ...p, entity_type_id: v } : null))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {bundle.entity_types.map((et) => (
                      <SelectItem key={et.id} value={et.id} className="text-xs font-mono">
                        {et.slug} — {et.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={draft.description ?? ""}
                  onChange={(e) =>
                    setDraft((p) => (p ? { ...p, description: e.target.value } : null))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                disabled={busy || draft.canonical_name.trim().length === 0}
                onClick={() => void submit({ ...draft, source: SOURCE_USER })}
              >
                {busy ? <Loader2 className="animate-spin" /> : null}
                Save entity
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <ConfirmDangerDialog
        open={toDelete != null}
        title="Delete entity?"
        description="Hard-deletes cascaded aliases, identifiers, topic + entity edges."
        confirmLabel={busy ? "…" : "Delete entity"}
        disabled={busy}
        onCancel={() => setToDelete(null)}
        onConfirm={() => void confirmDel()}
      />
    </div>
  );
}

function AliasesPanel({
  kbId,
  aliases,
  entities,
  onSaved,
}: {
  kbId: string;
  aliases: KbAliasView[];
  entities: KbEntityView[];
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<{
    original?: KbAliasView;
    body: UpsertKbAliasRequest;
  } | null>(null);
  const [aliasToDelete, setAliasToDelete] = useState<KbAliasView | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      await upsertAlias(kbId, { ...draft.body, source: SOURCE_USER });
      toast.success(draft.original ? "Alias updated" : "Alias created");
      setDraft(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!aliasToDelete) return;
    setBusy(true);
    try {
      await deleteAlias(kbId, aliasToDelete.id);
      toast.success("Alias deleted");
      setAliasToDelete(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  if (entities.length === 0) {
    return <EmptyState>Add an entity before linking aliases.</EmptyState>;
  }

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <Button
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={() =>
            setDraft({
              body: {
                entity_id: entities[0].id,
                alias: "",
                language: "",
                confidence: 1,
                is_ambiguous: false,
                source: SOURCE_USER,
              },
            })
          }
        >
          <Plus className="h-3 w-3" />
          Add alias
        </Button>
      </div>
      {aliases.length === 0 ? (
        <EmptyState>No aliases yet.</EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Actions</TableHead>
              <TableHead>Alias</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aliases.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mr-1 h-6 px-2 text-[10px]"
                    onClick={() =>
                      setDraft({
                        original: a,
                        body: {
                          entity_id: a.entity_id,
                          alias: a.alias,
                          language: a.language ?? "",
                          confidence: a.confidence,
                          is_ambiguous: a.is_ambiguous,
                          source: SOURCE_USER,
                        },
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-destructive"
                    onClick={() => setAliasToDelete(a)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
                <TableCell className="font-medium">{a.alias}</TableCell>
                <TableCell className="text-xs">
                  {entities.find((e) => e.id === a.entity_id)?.canonical_name ?? a.entity_id}
                </TableCell>
                <TableCell className="text-xs">{a.confidence}</TableCell>
                <TableCell>
                  <SourceBadge source={a.source} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {draft ? (
        <Dialog open onOpenChange={(o) => (!o ? setDraft(null) : null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{draft.original ? "Edit alias" : "Add alias"}</DialogTitle>
              <DialogDescription>
                Upsert identity is `(entity_id, alias)` — the alias string stays fixed while editing
                meta.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Entity</Label>
                <Select
                  value={draft.body.entity_id}
                  onValueChange={(v) =>
                    setDraft((d) => (d ? { ...d, body: { ...d.body, entity_id: v } } : null))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {entities.map((e) => (
                      <SelectItem key={e.id} value={e.id} className="text-xs">
                        {e.canonical_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Alias text</Label>
                <Input
                  value={draft.body.alias}
                  disabled={Boolean(draft.original)}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, body: { ...d.body, alias: e.target.value } } : null,
                    )
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Language (optional)</Label>
                <Input
                  value={draft.body.language ?? ""}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? { ...d, body: { ...d.body, language: e.target.value || undefined } }
                        : null,
                    )
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={draft.body.is_ambiguous ?? false}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, body: { ...d.body, is_ambiguous: e.target.checked } } : null,
                      )
                    }
                  />
                  Ambiguous
                </label>
              </div>
              <div className="space-y-1">
                <Label>Confidence 0–1</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  max={1}
                  value={draft.body.confidence ?? 1}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            body: { ...d.body, confidence: Number.parseFloat(e.target.value) || 0 },
                          }
                        : null,
                    )
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                disabled={busy || draft.body.alias.trim().length === 0}
                onClick={() => void save()}
              >
                Save alias
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <ConfirmDangerDialog
        open={aliasToDelete != null}
        title="Remove alias?"
        description="Alias row is permanently deleted."
        confirmLabel={busy ? "…" : "Remove alias"}
        disabled={busy}
        onCancel={() => setAliasToDelete(null)}
        onConfirm={() => void del()}
      />
    </div>
  );
}

function IdentifiersPanel({
  kbId,
  identifiers,
  entities,
  onSaved,
}: {
  kbId: string;
  identifiers: KbIdentifierView[];
  entities: KbEntityView[];
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<UpsertKbIdentifierRequest | null>(null);
  const [toDelete, setToDelete] = useState<KbIdentifierView | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      await upsertIdentifier(kbId, {
        ...draft,
        source: SOURCE_USER,
        market: draft.market?.trim() || undefined,
      });
      toast.success("Identifier saved");
      setDraft(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!toDelete) return;
    setBusy(true);
    try {
      await deleteIdentifier(kbId, toDelete.id);
      toast.success("Identifier deleted");
      setToDelete(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  const initType: KbIdentifierKind = KB_IDENTIFIER_TYPES[0];

  if (entities.length === 0) {
    return <EmptyState>Add entities first.</EmptyState>;
  }

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <Button
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={() =>
            setDraft({
              entity_id: entities[0].id,
              identifier_type: initType,
              identifier_value: "",
              market: "",
              source: SOURCE_USER,
            })
          }
        >
          <Plus className="h-3 w-3" /> Add identifier
        </Button>
      </div>
      {identifiers.length === 0 ? (
        <EmptyState>No identifiers linked yet.</EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Actions</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Market</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {identifiers.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mr-1 h-6 px-2 text-[10px]"
                    onClick={() =>
                      setDraft({
                        entity_id: row.entity_id,
                        identifier_type: row.identifier_type as KbIdentifierView["identifier_type"],
                        identifier_value: row.identifier_value,
                        market: row.market,
                        source: SOURCE_USER,
                      })
                    }
                  >
                    Edit / re-assign
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-destructive"
                    onClick={() => setToDelete(row)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
                <TableCell className="font-mono text-xs">{row.identifier_type}</TableCell>
                <TableCell className="font-mono text-xs">{row.identifier_value}</TableCell>
                <TableCell className="text-xs">
                  {entities.find((e) => e.id === row.entity_id)?.canonical_name ?? row.entity_id}
                </TableCell>
                <TableCell className="text-xs">{row.market}</TableCell>
                <TableCell>
                  <SourceBadge source={row.source} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {draft ? (
        <Dialog open onOpenChange={(o) => (!o ? setDraft(null) : null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create / update identifier</DialogTitle>
              <DialogDescription>
                Dedup on `(identifier_type, identifier_value, market)` — changing `entity_id` moves
                attribution.
              </DialogDescription>
            </DialogHeader>
            <IdentifierFormDraft draft={draft} entities={entities} onChange={(d) => setDraft(d)} />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button
                disabled={busy || draft.identifier_value.trim().length === 0}
                onClick={() => void save()}
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <ConfirmDangerDialog
        open={toDelete != null}
        description="Deletes this identifier triple."
        confirmLabel="Delete identifier"
        onCancel={() => setToDelete(null)}
        onConfirm={() => void del()}
        disabled={busy}
        title="Remove identifier?"
      />
    </div>
  );
}

function IdentifierFormDraft({
  draft,
  entities,
  onChange,
}: {
  draft: UpsertKbIdentifierRequest;
  entities: KbEntityView[];
  onChange: (d: UpsertKbIdentifierRequest) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label>Entity</Label>
        <Select value={draft.entity_id} onValueChange={(v) => onChange({ ...draft, entity_id: v })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {entities.map((e) => (
              <SelectItem key={e.id} value={e.id} className="text-xs">
                {e.canonical_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Type</Label>
        <Select
          value={draft.identifier_type}
          onValueChange={(v) => onChange({ ...draft, identifier_type: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...KB_IDENTIFIER_TYPES].map((t) => (
              <SelectItem key={t} value={t} className="text-xs font-mono">
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Value</Label>
        <Input
          value={draft.identifier_value}
          onChange={(e) => onChange({ ...draft, identifier_value: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label>Market (ISO / exchange code)</Label>
        <Input
          value={draft.market ?? ""}
          onChange={(e) => onChange({ ...draft, market: e.target.value })}
        />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────── Relations
function RelationsPanel({
  kbId,
  bundle,
  onSaved,
}: {
  kbId: string;
  bundle: KbTaxonomyBundle;
  onSaved: () => Promise<void>;
}) {
  const [topicDraft, setTopicDraft] = useState<UpsertKbTopicRelationRequest | null>(null);
  const [entityDraft, setEntityDraft] = useState<UpsertKbEntityRelationRequest | null>(null);
  const [delTopicRel, setDelTopicRel] = useState<KbTopicRelationView | null>(null);
  const [delEntityRel, setDelEntityRel] = useState<KbEntityRelationView | null>(null);
  const [busy, setBusy] = useState(false);

  const topicById = useMemo(() => new Map(bundle.topics.map((t) => [t.id, t])), [bundle.topics]);
  const entityById = useMemo(
    () => new Map(bundle.entities.map((e) => [e.id, e])),
    [bundle.entities],
  );

  async function submitTopic(rel: UpsertKbTopicRelationRequest) {
    setBusy(true);
    try {
      await upsertTopicRelation(kbId, rel);
      toast.success("Entity↔topic link saved");
      setTopicDraft(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitEntity(rel: UpsertKbEntityRelationRequest) {
    setBusy(true);
    try {
      await upsertEntityRelation(kbId, rel);
      toast.success("Entity↔entity link saved");
      setEntityDraft(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function doDelTopic() {
    if (!delTopicRel) return;
    setBusy(true);
    try {
      await deleteTopicRelation(kbId, delTopicRel.id);
      toast.success("Relation removed");
      setDelTopicRel(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  async function doDelEntity() {
    if (!delEntityRel) return;
    setBusy(true);
    try {
      await deleteEntityRelation(kbId, delEntityRel.id);
      toast.success("Relation removed");
      setDelEntityRel(null);
      await onSaved();
    } catch (e) {
      toast.error(mutationError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-8">
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-medium">Entity ↔ topic</h4>
          <Button
            size="sm"
            className="h-7 gap-1 text-xs"
            disabled={bundle.entities.length === 0 || bundle.topics.length === 0}
            onClick={() =>
              setTopicDraft({
                entity_id: bundle.entities[0]?.id ?? "",
                topic_id: bundle.topics[0]?.id ?? "",
                relation_type: null,
                confidence: null,
                source: SOURCE_USER,
              })
            }
          >
            <Plus className="h-3 w-3" />
            Link entity to topic
          </Button>
        </div>
        {bundle.topic_relations.length === 0 ? (
          <EmptyState>No links yet.</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">Actions</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Topic</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bundle.topic_relations.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[10px]"
                      onClick={() =>
                        setTopicDraft({
                          entity_id: r.entity_id,
                          topic_id: r.topic_id,
                          relation_type: r.relation_type,
                          confidence: r.confidence,
                          source: SOURCE_USER,
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[10px] text-destructive"
                      onClick={() => setDelTopicRel(r)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                  <TableCell>
                    {entityById.get(r.entity_id)?.canonical_name ?? r.entity_id}
                  </TableCell>
                  <TableCell>{topicById.get(r.topic_id)?.name ?? r.topic_id}</TableCell>
                  <TableCell>{r.relation_type ?? "—"}</TableCell>
                  <TableCell className="text-xs">
                    {r.confidence == null ? "—" : r.confidence.toFixed(3)}
                  </TableCell>
                  <TableCell>
                    <SourceBadge source={r.source} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-medium">Entity ↔ entity</h4>
          <Button
            size="sm"
            disabled={bundle.entities.length < 2}
            className="h-7 gap-1 text-xs"
            onClick={() =>
              setEntityDraft({
                source_entity_id: bundle.entities[0]?.id ?? "",
                target_entity_id: bundle.entities[1]?.id ?? bundle.entities[0]?.id ?? "",
                relation_type: "other",
                description: "",
                confidence: null,
                source: SOURCE_USER,
              })
            }
          >
            <Plus className="h-3 w-3" />
            Link entities
          </Button>
        </div>
        {bundle.entity_relations.length === 0 ? (
          <EmptyState>No entity↔entity relations yet.</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">Actions</TableHead>
                <TableHead>Source entity</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Target entity</TableHead>
                <TableHead className="hidden md:table-cell">Description</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bundle.entity_relations.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[10px]"
                      onClick={() =>
                        setEntityDraft({
                          source_entity_id: r.source_entity_id,
                          target_entity_id: r.target_entity_id,
                          relation_type: r.relation_type,
                          description: r.description ?? "",
                          confidence: r.confidence,
                          source: SOURCE_USER,
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-destructive"
                      onClick={() => setDelEntityRel(r)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                  <TableCell>
                    {entityById.get(r.source_entity_id)?.canonical_name ?? r.source_entity_id}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.relation_type}</TableCell>
                  <TableCell>
                    {entityById.get(r.target_entity_id)?.canonical_name ?? r.target_entity_id}
                  </TableCell>
                  <TableCell className="hidden text-xs md:table-cell">
                    {r.description || "—"}
                  </TableCell>
                  <TableCell>
                    <SourceBadge source={r.source} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {topicDraft ? (
        <TopicRelationDialog
          open
          initial={topicDraft}
          submitting={busy}
          entities={bundle.entities}
          topics={bundle.topics}
          onCancel={() => setTopicDraft(null)}
          onSubmit={(b) => void submitTopic({ ...b, source: SOURCE_USER })}
        />
      ) : null}

      {entityDraft ? (
        <EntityRelationDialog
          open
          kbId={kbId}
          initial={entityDraft}
          submitting={busy}
          entities={bundle.entities}
          onCancel={() => setEntityDraft(null)}
          onSubmit={(b) =>
            void submitEntity({
              ...b,
              source: SOURCE_USER,
            })
          }
        />
      ) : null}

      <ConfirmDangerDialog
        open={delTopicRel != null}
        title="Remove link?"
        description="Deletes this entity-topic edge."
        disabled={busy}
        onCancel={() => setDelTopicRel(null)}
        confirmLabel={busy ? "…" : "Remove"}
        onConfirm={() => void doDelTopic()}
      />

      <ConfirmDangerDialog
        open={delEntityRel != null}
        title="Remove link?"
        description="Deletes this entity-entity predicate edge."
        disabled={busy}
        onCancel={() => setDelEntityRel(null)}
        confirmLabel={busy ? "…" : "Remove"}
        onConfirm={() => void doDelEntity()}
      />
    </div>
  );
}

function TopicRelationDialog(props: {
  open: boolean;
  initial: UpsertKbTopicRelationRequest;
  entities: KbEntityView[];
  topics: KbTopicView[];
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (b: UpsertKbTopicRelationRequest) => void;
}) {
  const [entityId, setEntityId] = useState(props.initial.entity_id);
  const [topicId, setTopicId] = useState(props.initial.topic_id);
  const [relType, setRelType] = useState<string>(
    props.initial.relation_type === null ? "" : (props.initial.relation_type ?? ""),
  );
  const [confRaw, setConfRaw] = useState(
    props.initial.confidence == null ? "" : String(props.initial.confidence),
  );

  useEffect(() => {
    if (!props.open) return;
    setEntityId(props.initial.entity_id);
    setTopicId(props.initial.topic_id);
    setRelType(props.initial.relation_type === null ? "" : (props.initial.relation_type ?? ""));
    setConfRaw(props.initial.confidence == null ? "" : String(props.initial.confidence));
  }, [
    props.open,
    props.initial.entity_id,
    props.initial.topic_id,
    props.initial.relation_type,
    props.initial.confidence,
  ]);

  const disabled = props.entities.length === 0 || props.topics.length === 0;

  const trimmedConf = confRaw.trim();
  let confidenceInvalid = false;
  if (trimmedConf.length > 0) {
    const n = Number.parseFloat(trimmedConf);
    confidenceInvalid = !Number.isFinite(n) || n < 0 || n > 1;
  }

  const canSave =
    entityId.length > 0 &&
    topicId.length > 0 &&
    !confidenceInvalid &&
    !disabled &&
    !props.submitting;

  return (
    <Dialog open={props.open} onOpenChange={(o) => (!o ? props.onCancel() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Entity-topic link</DialogTitle>
          <DialogDescription>Upsert keyed by `(entity_id, topic_id)`.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Entity</Label>
            <Select value={entityId} onValueChange={setEntityId} disabled={disabled}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {props.entities.map((e) => (
                  <SelectItem key={e.id} value={e.id} className="text-xs">
                    {e.canonical_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Topic</Label>
            <Select value={topicId} onValueChange={setTopicId} disabled={disabled}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {props.topics.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-xs">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Relation kind (optional)</Label>
            <Select
              value={relType || "__empty__"}
              onValueChange={(v) => setRelType(v === "__empty__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Unset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__empty__" className="text-xs">
                  — none —
                </SelectItem>
                {[...KB_ENTITY_TOPIC_RELATION_TYPES].map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Confidence (0–1, optional)</Label>
            <Input
              value={confRaw}
              onChange={(e) => setConfRaw(e.target.value)}
              placeholder="omit = null"
            />
            {confidenceInvalid ? (
              <p className="text-[11px] text-destructive">Confidence must be a number in [0, 1].</p>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={props.onCancel} disabled={props.submitting}>
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={() =>
              props.onSubmit({
                entity_id: entityId,
                topic_id: topicId,
                relation_type: relType.trim().length === 0 ? null : relType.trim(),
                confidence: confRaw.trim().length === 0 ? null : Number.parseFloat(confRaw.trim()),
              })
            }
          >
            Save link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntityRelationDialog(props: {
  open: boolean;
  kbId: string;
  initial: UpsertKbEntityRelationRequest;
  entities: KbEntityView[];
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (b: UpsertKbEntityRelationRequest) => void;
}) {
  const [sourceId, setSourceId] = useState(props.initial.source_entity_id);
  const [targetId, setTargetId] = useState(props.initial.target_entity_id);
  const [relType, setRelType] = useState(props.initial.relation_type);
  const [desc, setDesc] = useState(props.initial.description ?? "");
  const [confRaw, setConfRaw] = useState(
    props.initial.confidence == null ? "" : String(props.initial.confidence),
  );

  useEffect(() => {
    if (!props.open) return;
    setSourceId(props.initial.source_entity_id);
    setTargetId(props.initial.target_entity_id);
    setRelType(props.initial.relation_type);
    setDesc(props.initial.description ?? "");
    setConfRaw(props.initial.confidence == null ? "" : String(props.initial.confidence));
  }, [
    props.open,
    props.initial.source_entity_id,
    props.initial.target_entity_id,
    props.initial.relation_type,
    props.initial.description,
    props.initial.confidence,
  ]);

  const selfLoop = sourceId === targetId;
  const confParsed = confRaw.trim().length === 0 ? null : Number.parseFloat(confRaw.trim());
  const confOk =
    confParsed === null || (Number.isFinite(confParsed) && confParsed >= 0 && confParsed <= 1);

  return (
    <Dialog open={props.open} onOpenChange={(o) => (!o ? props.onCancel() : null)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Entity-edge</DialogTitle>
          <DialogDescription>
            Dedup `(source_entity_id, target_entity_id, relation_type)` — KB{" "}
            <span className="font-mono text-[11px]">{props.kbId.slice(0, 8)}…</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Source entity</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {props.entities.map((e) => (
                    <SelectItem key={e.id} value={e.id} className="text-xs">
                      {e.canonical_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Target entity</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {props.entities.map((e) => (
                    <SelectItem key={e.id} value={e.id} className="text-xs">
                      {e.canonical_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selfLoop ? (
            <p className="text-xs text-destructive">Edges cannot loop to the same entity.</p>
          ) : null}
          <div className="space-y-1">
            <Label>Relation predicate</Label>
            <Select value={relType} onValueChange={setRelType}>
              <SelectTrigger className="font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...KB_ENTITY_RELATION_TYPES].map((t) => (
                  <SelectItem key={t} value={t} className="text-xs font-mono">
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Confidence (optional)</Label>
            <Input value={confRaw} onChange={(e) => setConfRaw(e.target.value)} />
            {!confOk ? (
              <p className="text-[11px] text-destructive">Must be numeric in [0,1].</p>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={props.onCancel} disabled={props.submitting}>
            Cancel
          </Button>
          <Button
            disabled={props.submitting || selfLoop || !confOk || relType.trim().length === 0}
            onClick={() =>
              props.onSubmit({
                source_entity_id: sourceId,
                target_entity_id: targetId,
                relation_type: relType,
                description: desc.trim() === "" ? null : desc.trim(),
                confidence: confRaw.trim().length === 0 ? null : Number.parseFloat(confRaw.trim()),
              })
            }
          >
            Save edge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────── Predicates (read-only)
function PredicatesTable({ predicates }: { predicates: KbPredicateView[] }) {
  if (predicates.length === 0)
    return (
      <EmptyState>
        No predicates registered — promote from Extraction tab or seed via worker.
      </EmptyState>
    );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Slug</TableHead>
          <TableHead>Subject types</TableHead>
          <TableHead>Object types</TableHead>
          <TableHead>Source</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {predicates.map((p) => (
          <TableRow key={p.id}>
            <TableCell className="font-medium">{p.name}</TableCell>
            <TableCell className="font-mono text-xs">{p.slug}</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {p.subject_types.length === 0 ? "—" : p.subject_types.join(", ")}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {p.object_types.length === 0 ? "—" : p.object_types.join(", ")}
            </TableCell>
            <TableCell>
              <SourceBadge source={p.source} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Shared confirm — red primary action. */
function ConfirmDangerDialog({
  open,
  title,
  description,
  confirmLabel,
  disabled,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  disabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onCancel() : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={disabled} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SourceBadge({ source }: { source: TaxonomySource }) {
  if (source === "system")
    return (
      <Badge variant="outline" className="text-[10px]">
        system
      </Badge>
    );
  if (source === "ai")
    return (
      <Badge variant="secondary" className="text-[10px]">
        ai
      </Badge>
    );
  if (source === "user")
    return (
      <Badge variant="default" className="text-[10px]">
        user
      </Badge>
    );
  return (
    <Badge variant="outline" className="text-[10px]">
      {source}
    </Badge>
  );
}
